import { useCallback, useEffect, useState } from "react"

const LOCK_TTL = 60 * 1000

const getLockKey = (uniqueId) => `assistant_lock_${uniqueId}`

const readLock = (uniqueId) => {
    if (!uniqueId) return false

    const raw = localStorage.getItem(getLockKey(uniqueId))
    if (!raw) return false

    try {
        const { expiresAt } = JSON.parse(raw)
        if (Date.now() > expiresAt) {
            localStorage.removeItem(getLockKey(uniqueId))
            return false
        }
        return true
    } catch {
        localStorage.removeItem(getLockKey(uniqueId))
        return false
    }
}

// Persistent, per-ticket asking lock with a TTL that survives page reloads -
// only meaningful for the sidebar surface (a ticket's uniqueid). Distinct
// from the plain in-flight "awaiting an answer" flag every surface has,
// which is just local component state.
const useAssistantLock = (uniqueId) => {
    const [locked, setLocked] = useState(() => readLock(uniqueId))

    // uniqueId changes whenever the operator switches tickets - re-read that
    // ticket's own lock state rather than keeping the previous ticket's.
    useEffect(() => {
        setLocked(readLock(uniqueId))
    }, [uniqueId])

    const lock = useCallback(() => {
        if (!uniqueId) return
        localStorage.setItem(getLockKey(uniqueId), JSON.stringify({ expiresAt: Date.now() + LOCK_TTL }))
        setLocked(true)
    }, [uniqueId])

    const unlock = useCallback(() => {
        if (uniqueId)
            localStorage.removeItem(getLockKey(uniqueId))
        setLocked(false)
    }, [uniqueId])

    return [locked, lock, unlock]
}

export default useAssistantLock
